/**
 * Administración de Control de Kits — port de kit-stock/src/pages/Admin.tsx.
 * Sección Clapbases completa (CRUD + sync manual de posiciones). La sección de
 * usuarios del original se reemplaza por la lista de managers por Clapbase:
 * el login es el de la plataforma y admin/tech/people son globales.
 */

import { toastError } from '@/lib/toast-error';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Plus, Building2, CheckCircle2, XCircle, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import type { KitOfficeSummary } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';

const parseManagers = (raw: string): string[] =>
  raw
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);

const managersField = z
  .string()
  .optional()
  .refine(
    (raw) => parseManagers(raw ?? '').every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)),
    'Hay un email inválido en la lista'
  );

function CreateOfficeModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const mutation = trpc.kits.createOffice.useMutation();

  const form = useForm({
    resolver: zodResolver(z.object({
      name: z.string().min(1, 'Requerido'),
      safetyStock: z.coerce.number().min(0, 'Debe ser mayor o igual a 0'),
      openPositions: z.coerce.number().min(0, 'Debe ser mayor o igual a 0'),
      huntingSheetSite: z.string().optional(),
      initialStock: z.coerce.number().min(0, 'Debe ser mayor o igual a 0').optional(),
      managers: managersField,
    })),
    defaultValues: { name: '', safetyStock: 10, openPositions: 0, huntingSheetSite: '', initialStock: 0, managers: '' },
  });

  const onSubmit = (values: any) => {
    mutation.mutate(
      {
        name: values.name,
        safetyStock: values.safetyStock,
        openPositions: values.openPositions,
        huntingSheetSite: values.huntingSheetSite?.trim() || undefined,
        initialStock: values.initialStock,
        managers: parseManagers(values.managers ?? ''),
      },
      {
        onSuccess: () => {
          toast.success(t('kits.admin.clapbase_creada'));
          utils.kits.listOffices.invalidate();
          onOpenChange(false);
          form.reset();
        },
        onError: (e) => toastError(e, 'common.toast.error_al_crear'),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.admin.crear_clapbase')}</DialogTitle>
          <DialogDescription>{t('kits.admin.anade_una_nueva_oficina_al_sistema')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.nombre')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="safetyStock" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.admin.stock_de_seguridad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="huntingSheetSite" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.admin.clapbase_en_hoja_offers_and_hires')} <span className="text-muted-foreground font-normal">(opcional)</span></FormLabel>
                <FormControl><Input placeholder={t('kits.admin.ej_buenos_aires_mexico_city_bogota')} {...field} /></FormControl>
                <p className="text-xs text-muted-foreground">{t('kits.admin.si_se_configura_las_posiciones_confirmadas')}</p>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="openPositions" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.admin.posiciones_confirmadas_manual_fallback')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="initialStock" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.admin.stock_inicial_opcional')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="managers" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.admin.office_managers')} <span className="text-muted-foreground font-normal">{t('kits.admin.emails_separados_por_coma')}</span></FormLabel>
                {/* i18n-ok: ejemplo de emails, no es prosa traducible */}
                <FormControl><Input placeholder="ana@winclap.com, luis@winclap.com" {...field} /></FormControl>
                <p className="text-xs text-muted-foreground">{t('kits.admin.estos_emails_ven_y_operan_solo')}</p>
                <FormMessage />
              </FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('common.ui.create')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function EditOfficeModal({ open, onOpenChange, office }: { open: boolean; onOpenChange: (v: boolean) => void; office: KitOfficeSummary }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const mutation = trpc.kits.updateOffice.useMutation();
  const { data: detail } = trpc.kits.getOfficeAdmin.useQuery({ officeId: office.officeId }, { enabled: open });

  const form = useForm({
    resolver: zodResolver(z.object({
      name: z.string().min(1, 'Requerido'),
      safetyStock: z.coerce.number().min(0),
      openPositions: z.coerce.number().min(0),
      huntingSheetSite: z.string().optional(),
      active: z.boolean(),
      managers: managersField,
    })),
    defaultValues: {
      name: office.name,
      safetyStock: office.safetyStock,
      openPositions: office.openPositions,
      huntingSheetSite: office.huntingSheetSite ?? '',
      active: office.active,
      managers: '',
    },
    values: detail
      ? {
          name: detail.name,
          safetyStock: detail.safetyStock,
          openPositions: detail.openPositions,
          huntingSheetSite: detail.huntingSheetSite ?? '',
          active: detail.active,
          managers: (detail.managers ?? []).join(', '),
        }
      : undefined,
  });

  const onSubmit = (values: any) => {
    // String vacío → null para que el server borre el mapeo de la hoja
    mutation.mutate(
      {
        officeId: office.officeId,
        name: values.name,
        safetyStock: values.safetyStock,
        openPositions: values.openPositions,
        huntingSheetSite: values.huntingSheetSite?.trim() || null,
        active: values.active,
        managers: parseManagers(values.managers ?? ''),
      },
      {
        onSuccess: () => {
          toast.success(t('kits.admin.clapbase_actualizada'));
          utils.kits.listOffices.invalidate();
          utils.kits.getOfficeAdmin.invalidate({ officeId: office.officeId });
          onOpenChange(false);
        },
        onError: (e) => toastError(e, 'common.toast.error_al_actualizar'),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.admin.editar_clapbase')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.common.nombre')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="safetyStock" render={({ field }) => (
              <FormItem><FormLabel>{t('kits.admin.stock_de_seguridad')}</FormLabel><FormControl><Input type="number" {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <FormField control={form.control} name="huntingSheetSite" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.admin.clapbase_en_hoja_offers_and_hires')} <span className="text-muted-foreground font-normal">(opcional)</span></FormLabel>
                <FormControl><Input placeholder={t('kits.admin.ej_buenos_aires_mexico_city_bogota')} {...field} value={field.value ?? ''} /></FormControl>
                <p className="text-xs text-muted-foreground">{t('kits.admin.si_se_configura_las_posiciones_confirmadas')}</p>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="openPositions" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.admin.posiciones_confirmadas')} <span className="text-muted-foreground font-normal">{t('kits.admin.fallback_manual')}</span></FormLabel>
                <FormControl><Input type="number" {...field} /></FormControl>
                <p className="text-xs text-muted-foreground">{t('kits.admin.se_usa_solo_si_no_hay')}</p>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="managers" render={({ field }) => (
              <FormItem>
                <FormLabel>{t('kits.admin.office_managers')} <span className="text-muted-foreground font-normal">{t('kits.admin.emails_separados_por_coma')}</span></FormLabel>
                {/* i18n-ok: ejemplo de emails, no es prosa traducible */}
                <FormControl><Input placeholder="ana@winclap.com, luis@winclap.com" {...field} /></FormControl>
                <p className="text-xs text-muted-foreground">{t('kits.admin.estos_emails_ven_y_operan_solo')}</p>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="active" render={({ field }) => (
              <FormItem className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <FormLabel>{t('kits.common.activa')}</FormLabel>
                  <p className="text-sm text-muted-foreground">{t('kits.admin.desactivar_esta_oficina_la_oculta_del')}</p>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )} />
            <Button type="submit" disabled={mutation.isPending} className="w-full">{t('kits.admin.guardar_cambios')}</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function KitsAdmin() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [modals, setModals] = useState<{ createOffice: boolean; editOffice: KitOfficeSummary | null }>({
    createOffice: false,
    editOffice: null,
  });

  const { data: offices, isLoading: loadingOffices } = trpc.kits.listOffices.useQuery({ includeInactive: true });
  const syncMutation = trpc.kits.syncOpenPositions.useMutation();

  const handleSync = () => {
    syncMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('kits.admin.cache_actualizado_las_posiciones_confirmadas_se'));
        utils.kits.listOffices.invalidate();
      },
      onError: () => toast.error(t('kits.admin.error_al_actualizar_el_cache_de')),
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('kits.admin.administracion')}</h1>
        <p className="text-muted-foreground mt-1">{t('kits.admin.gestion_global_de_clapbases_y_accesos')}</p>
      </div>

      {/* Clapbases */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6" /> {t('kits.common.clapbases')}</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSync} disabled={syncMutation.isPending}>
              <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {t('kits.admin.sincronizacion_manual_posiciones')}
            </Button>
            <Button onClick={() => setModals({ ...modals, createOffice: true })}><Plus className="mr-2 h-4 w-4" /> {t('kits.admin.crear_clapbase')}</Button>
          </div>
        </div>

        <Card className="shadow-xs overflow-hidden">
          {loadingOffices ? (
            <div className="p-6 space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !offices || offices.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">{t('kits.admin.no_hay_clapbases')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('kits.common.nombre')}</TableHead>
                    <TableHead className="text-right">{t('kits.common.stock')}</TableHead>
                    <TableHead className="text-right">{t('kits.common.seguridad')}</TableHead>
                    <TableHead className="text-right">{t('kits.common.entrante')}</TableHead>
                    <TableHead className="text-right">{t('kits.common.posiciones')}</TableHead>
                    <TableHead>{t('kits.common.fuente')}</TableHead>
                    <TableHead>{t('kits.admin.estado_stock')}</TableHead>
                    <TableHead>{t('kits.common.activa')}</TableHead>
                    <TableHead className="text-right">{t('kits.admin.pedido_recomendado')}</TableHead>
                    <TableHead className="text-right">{t('kits.common.acciones')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offices.map((office) => (
                    <TableRow key={office.officeId}>
                      <TableCell className="font-bold">
                        <div>{office.name}</div>
                        {office.huntingSheetSite && (
                          <div className="text-xs text-muted-foreground mt-0.5">Hunting: {office.huntingSheetSite}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{office.stock}</TableCell>
                      <TableCell className="text-right">{office.safetyStock}</TableCell>
                      <TableCell className="text-right">{office.incoming}</TableCell>
                      <TableCell className="text-right">{office.openPositions}</TableCell>
                      <TableCell>
                        {office.openPositionsSource === 'sheet' ? (
                          <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">{t('kits.common.hoja')}</Badge>
                        ) : office.openPositionsSource === 'sheet_error' ? (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1">
                            <AlertTriangle className="h-3 w-3" /> {t('kits.admin.hoja_sin_conexion')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500 border-gray-300">{t('kits.common.manual')}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={office.status === 'verde' ? 'bg-green-100 text-green-700' : office.status === 'naranja' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}>
                          {office.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {office.active ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{office.recommendedOrder}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => setModals({ ...modals, editOffice: office })}>{t('common.ui.edit')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Accesos: reemplaza el CRUD de usuarios del original */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20 p-4 flex items-start gap-3 text-sm">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
        <div className="text-blue-900 dark:text-blue-200">
          <p className="font-semibold">{t('kits.common.accesos')}</p>
          <p>
            {t('kits.admin.el_login_es_el_de_la')}
          </p>
        </div>
      </div>

      <CreateOfficeModal open={modals.createOffice} onOpenChange={(v) => setModals({ ...modals, createOffice: v })} />
      {modals.editOffice && (
        <EditOfficeModal
          open={!!modals.editOffice}
          onOpenChange={(v) => setModals({ ...modals, editOffice: v ? modals.editOffice : null })}
          office={modals.editOffice}
        />
      )}
    </div>
  );
}
